import { useCallback, useEffect, useState } from 'react';
import { listarAberturas } from '../../api/aberturas.js';
import type { DecisaoDaDivergencia, DivergenciaDecidida } from '../../api/aberturas.js';
import { intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { useConsulta } from '../../api/useConsulta.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeConfere } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { CartaoDeDivergencia } from './CartaoDeDivergencia.js';
import { DialogoDeDecisao, estaEmDivergencia } from './DialogoDeDecisao.js';
import type { AberturaEmDivergencia } from './DialogoDeDecisao.js';
import { formatarQuantidade } from './quantidade.js';

type Pedido = {
  readonly abertura: AberturaEmDivergencia;
  readonly decisao: DecisaoDaDivergencia;
};

/**
 * Divergências de quantidade pendentes (r1-mov, passo 2.2).
 *
 * A entrega que passou da quantidade iniciada fica retida e NÃO vai ao ERP
 * até alguém decidir — divergência nunca se resolve sozinha. A lista vem do
 * servidor (`status=EmDivergencia`), da mais antiga para a mais nova: a que
 * espera há mais tempo aparece primeiro.
 */
export function AbaDivergencias({ aoDecidir }: { readonly aoDecidir?: () => void }) {
  const [pagina, definirPagina] = useState(1);
  const [pedido, definirPedido] = useState<Pedido | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);

  const buscar = useCallback(() => listarAberturas({ status: 'EmDivergencia', pagina }), [pagina]);
  const consulta = useConsulta(buscar);
  const ultimaPagina = consulta.dados === null ? null : totalDePaginas(consulta.dados);

  // A decisão tira a divergência da lista: a página aberta pode esvaziar com
  // outras ainda esperando nas anteriores. "Nenhuma divergência" ali seria
  // falso — a tela volta para a última página que existe.
  useEffect(() => {
    if (ultimaPagina !== null && pagina > ultimaPagina) {
      definirPagina(ultimaPagina);
    }
  }, [pagina, ultimaPagina]);

  function decidido(decidida: DivergenciaDecidida, produto: string) {
    definirPedido(null);
    definirAviso(
      `Divergência de ${produto} decidida: a entrega vai ao ERP com ${formatarQuantidade(decidida.entrega.quantidade)}.`,
    );
    consulta.recarregar();
    aoDecidir?.();
  }

  return (
    <div className="movimentacoes__aba">
      {aviso !== null && <AvisoDaTela tom="ok">{aviso}</AvisoDaTela>}

      <Resultado
        consulta={consulta}
        espera="Carregando as divergências…"
        estaVazio={(dados) => dados.total === 0}
        vazio={
          <EstadoVazio
            icone={<IconeConfere tamanho={28} />}
            titulo="Nenhuma divergência esperando decisão."
            detalhe="Quando uma entrega passar da quantidade iniciada, ela fica retida aqui até alguém decidir."
          />
        }
      >
        {(dados) => (
          <div className="movimentacoes__divergencias">
            {dados.itens.filter(estaEmDivergencia).map((abertura) => (
              <CartaoDeDivergencia
                key={abertura.id}
                abertura={abertura}
                aoEscolher={(decisao) => {
                  definirAviso(null);
                  definirPedido({ abertura, decisao });
                }}
              />
            ))}
            <div className="movimentacoes__paginacao">
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'divergência', 'divergências')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            </div>
          </div>
        )}
      </Resultado>

      {pedido !== null && (
        <DialogoDeDecisao
          abertura={pedido.abertura}
          decisao={pedido.decisao}
          aoDecidir={(decidida) => {
            decidido(decidida, pedido.abertura.produto);
          }}
          aoCancelar={() => {
            definirPedido(null);
          }}
        />
      )}
    </div>
  );
}
